/* ============================================================
   SOLSTICE — global.js
   Utils + Custom Elements communs. 0 dépendance, tout en defer.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Utils ---------- */
  window.SolsticeUtils = {
    formatMoney(cents) {
      const format = window.Solstice.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const [int, dec] = value.split('.');
      const intComma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format
        .replace(/\{\{\s*amount\s*\}\}/, `${intComma}.${dec}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, intComma)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`);
    },
    toast(msg) {
      let el = document.querySelector('.toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'toast';
        el.setAttribute('role', 'status');
        document.body.appendChild(el);
      }
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(el._t);
      el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
    },
    trapFocus(container) {
      const focusables = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return () => {};
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const handler = (e) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      container.addEventListener('keydown', handler);
      first.focus();
      return () => container.removeEventListener('keydown', handler);
    },
    fetchSection(sectionId, url) {
      const u = new URL(url || window.location.href, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      return fetch(u).then((r) => r.text());
    }
  };

  /* ---------- Cart API (single source of truth) ---------- */
  window.SolsticeCart = {
    async add(items) {
      const res = await fetch(window.Solstice.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Add to cart failed');
      }
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add' } }));
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(window.Solstice.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const data = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'change' } }));
      return data;
    },
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },
    async updateNote(note) {
      await fetch(window.Solstice.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    }
  };

  /* ---------- Reveal on scroll ---------- */
  if (window.Solstice.settings.animations && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    const observeAll = (root) => root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => io.observe(el));
    observeAll(document);
    document.addEventListener('shopify:section:load', (e) => observeAll(e.target));
  } else {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Sticky header shadow ---------- */
  const headerSection = document.querySelector('.header');
  if (headerSection) {
    const onScroll = () => headerSection.classList.toggle('is-stuck', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Dark mode toggle ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-theme-toggle]');
    if (!t) return;
    const next = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('solstice:theme', next); } catch (err) {}
  });

  /* ---------- <details-disclosure> generic open/close helpers ---------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this._untrap = null;
      this.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      const overlay = this.querySelector('[data-drawer-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() {
      this._trigger = document.activeElement;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const panel = this.querySelector('[data-drawer-panel]') || this;
      this._untrap = window.SolsticeUtils.trapFocus(panel);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }
    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._untrap) this._untrap();
      if (this._trigger) this._trigger.focus();
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
  }
  if (!customElements.get('drawer-element')) customElements.define('drawer-element', DrawerElement);

  /* ---------- Quantity input ---------- */
  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.qty === 'plus' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const val = Math.max(min - (this.dataset.allowZero === 'true' ? 1 : 0), parseInt(this.input.value || '1', 10) + step);
          this.input.value = val;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  if (!customElements.get('qty-input')) customElements.define('qty-input', QtyInput);

  /* ---------- Header: mega menu (hover + keyboard) ---------- */
  class MegaNav extends HTMLElement {
    connectedCallback() {
      this.items = this.querySelectorAll('.nav__item--has-children');
      this.items.forEach((item) => {
        let timer;
        item.addEventListener('mouseenter', () => { clearTimeout(timer); this.closeAll(); item.classList.add('is-open'); });
        item.addEventListener('mouseleave', () => { timer = setTimeout(() => item.classList.remove('is-open'), 120); });
        const link = item.querySelector(':scope > .nav__link');
        if (link) {
          link.addEventListener('click', (e) => {
            if (link.getAttribute('href') === '#') { e.preventDefault(); }
            const open = item.classList.contains('is-open');
            this.closeAll();
            if (!open) item.classList.add('is-open');
            link.setAttribute('aria-expanded', String(!open));
          });
          link.addEventListener('keydown', (e) => { if (e.key === 'Escape') { item.classList.remove('is-open'); link.focus(); } });
        }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeAll(); });
    }
    closeAll() {
      this.items.forEach((i) => {
        i.classList.remove('is-open');
        const l = i.querySelector(':scope > .nav__link');
        if (l) l.setAttribute('aria-expanded', 'false');
      });
    }
  }
  if (!customElements.get('mega-nav')) customElements.define('mega-nav', MegaNav);

  /* ---------- Mobile menu drawer ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-menu]');
    if (opener) { const d = document.querySelector('menu-drawer'); if (d) d.open(); }
    const sub = e.target.closest('[data-submenu-toggle]');
    if (sub) {
      const li = sub.closest('.menu-drawer__item');
      li.classList.toggle('is-open');
      sub.setAttribute('aria-expanded', li.classList.contains('is-open'));
    }
  });
  class MenuDrawer extends DrawerElement {}
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Search modal ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-search]')) {
      const m = document.querySelector('search-modal');
      if (m) { m.open(); const i = m.querySelector('input[type="search"]'); if (i) setTimeout(() => i.focus(), 80); }
    }
  });
  class SearchModal extends DrawerElement {}
  if (!customElements.get('search-modal')) customElements.define('search-modal', SearchModal);

  /* ---------- Cart count live update ---------- */
  document.addEventListener('cart:updated', async () => {
    const cart = await window.SolsticeCart.get();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('hidden', cart.item_count === 0);
    });
  });

  /* ---------- Announcement rotator ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = this.querySelectorAll('.announcement__slide');
      if (this.slides.length < 2) return;
      this.i = 0;
      this.querySelector('[data-ann-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-ann-next]')?.addEventListener('click', () => this.go(1));
      this.timer = setInterval(() => this.go(1), parseInt(this.dataset.interval || '5000', 10));
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
    }
    go(dir) {
      this.slides[this.i].classList.remove('is-active');
      this.i = (this.i + dir + this.slides.length) % this.slides.length;
      this.slides[this.i].classList.add('is-active');
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Accordion (accessible) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.accordion__btn');
    if (!btn) return;
    const acc = btn.closest('.accordion');
    const body = acc.querySelector('.accordion__body');
    const open = acc.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open);
    if (body) body.hidden = !open;
  });

  /* ---------- Link prefetch fallback (hover) ---------- */
  if (!HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) {
    const seen = new Set();
    document.addEventListener('mouseover', (e) => {
      const a = e.target.closest('a[href^="/products/"], a[href^="/collections/"]');
      if (!a || seen.has(a.href)) return;
      seen.add(a.href);
      const l = document.createElement('link');
      l.rel = 'prefetch'; l.href = a.href;
      document.head.appendChild(l);
    }, { passive: true });
  }
})();

/* Autoplay muted ambient videos — IntersectionObserver driven (reliable on iOS).
   IntersectionObserver fires dependably during iOS momentum scrolling, unlike
   throttled scroll events. When any observed video enters the viewport we (re)play
   EVERY video whose vertical band is on screen — the vertical test means the
   off-centre cards of the horizontal "video row" carousel start together too.
   iOS robustness:
   - play() is retried several times (timed) and on canplay/loadeddata; iOS often
     rejects the first programmatic play() on a not-yet-ready muted video, which
     would otherwise leave it frozen on its poster with a native ▶ button.
   - we NEVER pause on touch devices: pausing a muted video on iOS suppresses its
     own re-autoplay (observed to break the video-story / video-row playback).
     On desktop we pause off-screen videos for perf.
   Scrub cinema videos (motion.js strips their `autoplay` on a true desktop
   pointer) are skipped via the hasAttribute guard. */
(function () {
  var isTouch = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  function prime(v) {
    v.muted = true; v.defaultMuted = true; v.setAttribute('muted', '');
    v.playsInline = true; v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
    if (v.preload === 'none' || v.preload === 'metadata') v.preload = 'auto';
  }
  function bandVisible(v) {
    var r = v.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.height > 0 && r.top < vh * 1.15 && r.bottom > vh * -0.15; // vertical band only
  }
  function attempt(v, tries) {
    if (!v.hasAttribute('autoplay') || !v.paused || !bandVisible(v)) return;
    var p = v.play();
    if (p && p.catch) p.catch(function () {
      if (tries > 0) setTimeout(function () { attempt(v, tries - 1); }, 350);
    });
  }
  function start(v) {
    if (!v.hasAttribute('autoplay')) return; // handed to the scrub controller
    prime(v);
    attempt(v, 8);
    var once = function () { attempt(v, 4); };
    v.addEventListener('canplay', once, { once: true });
    v.addEventListener('loadeddata', once, { once: true });
  }
  function playBand() {
    document.querySelectorAll('video[autoplay]').forEach(function (v) { if (bandVisible(v)) start(v); });
  }

  function init() {
    var vids = Array.prototype.slice.call(document.querySelectorAll('video[autoplay]'));
    if (!vids.length) return;
    vids.forEach(prime);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var anyIn = false;
        entries.forEach(function (e) {
          if (e.isIntersecting) { anyIn = true; }
          else if (!isTouch && !e.target.paused) { try { e.target.pause(); } catch (x) {} } // desktop perf only
        });
        if (anyIn) playBand();
      }, { threshold: 0, rootMargin: '150px 0px' });
      vids.forEach(function (v) { io.observe(v); });
    }

    // First interaction + resize + Shopify editor events nudge playback again.
    ['touchstart', 'pointerdown', 'click', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, playBand, { once: true, passive: true });
    });
    window.addEventListener('resize', playBand, { passive: true });
    document.addEventListener('shopify:section:load', playBand);
    playBand();
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
})();
